/**
 * The following events are emitted:
 * activated            param: geom
 * deactivated          param: geom
 * maximized            param: geom
 * minimized            param: geom
 * resized              param: geom
 * dragged              param: geom
 * opened               param: geom
 * closed               param: geom, save
 * close-request        param: geom, save
 */
<template lang="pug">

div.float(
  v-show="(!closeSelf && opened) || openedSelf",
  :style=`{
    position: absolute ? 'absolute' : 'relative',
    left    : geom.x.pos + 'px',
    top     : geom.y.pos + 'px',
    width   : geom.x.len + 'px',
    height  : geom.y.len + 'px',
    zIndex  : zIndex,
    opacity : dragging ? 0.6 : 1
  }`,
  @keyup.ctrl.enter.stop.prevent="_close($event, true)",
  @keyup.esc.stop.prevent="_close($event, false)")
  div.float__handle(
    v-if="resize && activated",
    v-for="handle in handles",
    :key="handle",
    :class="'float__handle_type_' + handle",
    @mousedown.stop.prevent="_mouseDown($event, handle)")
  div.float__header(
    v-if="headerIsShow",
    ref="floatHeader",
    :class="{'float__header_drag': drag}",
    :style=`{
      width: 100 + '%',
      height: hh + 'px'
    }`,
    @dblclick.stop.prevent="maximize && !headerBtnMax ? _maximize($event):null")
    slot(name="header")
      div.float__header-toolbar
        slot(name="header-toolbar")
          md-button.md-icon-button
              md-icon content_copy
          md-button.md-icon-button
              md-icon content_paste
          md-button.md-icon-button
              md-icon content_cut
          md-button.md-icon-button
              md-icon clear
      div.float__header-toolbar_main
        md-button.float__header-btn-max.md-icon-button.md-primary(
          v-if="headerBtnMax",
          :disabled="!maximize",
          @click.stop.prevent="_maximize($event)")
          md-icon {{ !maximized ? 'zoom_out_map' : 'fullscreen_exit' }}
          md-tooltip(md-direction="bottom")
            | {{ !maximized ? 'Maximize window' : 'Minimize window' }}
        md-button.float__header-btn-res.md-icon-button.md-primary(
          v-if="headerBtnRes",
          @click.stop.prevent="_close($event, true)")
          md-icon save
          md-tooltip(md-direction="bottom")
            | Save and Close (shortcut: ctrl+enter)
        md-button.float__header-btn-rej.md-icon-button.md-primary(
          v-if="headerBtnRej",
          @click.stop.prevent="_close($event, false)")
          md-icon cancel
          md-tooltip(md-direction="bottom")
            | Reject (shortcut: esc)
  div.float__content(
    :class="{'float__content_drag': drag && !headerIsShow}",
    :style=`{
      width: 100 +'%',
      height: headerIsShow ? (geom.y.len-hh)+'px' : geom.y.len+'px'
    }`,
    @dblclick.stop.prevent="maximize && !headerIsShow? _maximize($event): null")
    slot
      slot(name="content")
        md-input-container(
          style="min-width: 100%; min-height: 100%;",
          :md-input-invalid="false")
          md-textarea.float__text(
            v-model="value_",
            :debounce="300",
            :disabled="false",
            :maxlength="1000",
            :readonly="false",
            autofocus="autofocus")
          md-tooltip(
            v-if="error",
            md-direction="top",
            md-delay="100") Error. {{ error }}
</template>

<script>

import Geom from './utils/Geom.js'

export default {
  replace: true,
  name: 'v-float',
  props: {
    id           : {type: [String, Number], default: ''},
    opened       : {type: Boolean, default: true},
    closeSelf    : {type: Boolean, default: true},
    absolute     : {type: Boolean, default: true},
    drag         : {type: Boolean, default: true},
    resize       : {type: Boolean, default: true},
    maximize     : {type: Boolean, default: true},
    header       : {type: Boolean, default: true},
    headerBtnMax : {type: Boolean, default: true},
    headerBtnRes : {type: Boolean, default: true},
    headerBtnRej : {type: Boolean, default: true},
    axis         : {type: String,  default: 'both',
      validator: (v) => {return ['x', 'y', 'both'].indexOf(v)>=0 }
    },
    markers: {type: Array,
      default: () => ['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml']
    },
    x:     {type: Number, default:    0, validator: (v) => {return v >=0}},
    y:     {type: Number, default:    0, validator: (v) => {return v >=0}},
    w:     {type: Number, default:  200, validator: (v) => {return v >=0}},
    h:     {type: Number, default:  200, validator: (v) => {return v >=0}},
    hh:    {type: Number, default:   50, validator: (v) => {return v >=0}},
    hhmin: {type: Number, default:  150, validator: (v) => {return v >=0}},
    hwmin: {type: Number, default:  400, validator: (v) => {return v >=0}},
    minw:  {type: Number, default:  100, validator: (v) => {return v >=0}},
    minh:  {type: Number, default:  100, validator: (v) => {return v >=0}},
    maxw:  {type: Number, default: 9999, validator: (v) => {return v >=0}},
    maxh:  {type: Number, default: 9999, validator: (v) => {return v >=0}},
    deltx: {type: Number, default:   20, validator: (v) => {return v >=0}},
    delty: {type: Number, default:   20, validator: (v) => {return v >=0}},
    elP : {default: null},
    elC : {default: null},
    value: {default: ''},
    error: {default: ''}
  },
  data() {
    return {
      geom: {
        x: {
          pos: this.elC ? this.elC.getBoundingClientRect().left : this.x,
          len: this.elC ? this.elC.clientWidth : this.w
        },
        y: {
          pos: this.elC ? this.elC.getBoundingClientRect().top : this.y,
          len: this.elC ? this.elC.clientHeight : this.h
        },
      },
      openedSelf: true,
      activated : false,
      dragging  : false,
      resizing  : false,
      maximized : false,
      handles   : this.markers,
      handle    : null,
      zIndex    : 1,
      value_    : this.value
    }
  },
  computed: {
    headerIsShow() {
      if(!this.header)
        return false
      if(this.geom.x.len < this.hwmin)
        return false
      if(this.geom.y.len < this.hhmin)
        return false
      return true
    },
    sizeParent() {
      const elp = this.elP ? this.elP : document.body
      return {
        x: parseInt(elp.clientWidth, 10),
        y: parseInt(elp.clientHeight, 10)
      }
    },
    sizeLim() {
      const ext = this.sizeParent
      const min = {
        x: this.minw <= ext.x ? this.minw : ext.x,
        y: this.minh <= ext.y ? this.minh : ext.y
      }
      const max = {
        x: this.maxw >= min.x ? this.maxw : min.x,
        x: this.maxh >= min.y ? this.maxh : min.y
      }
      return {
        x : {min: min.x, max: max.x},
        y : {min: min.y, max: max.y}
      }
    }
  },
  created() {
    for(let h of ['tl', 'tm', 'tr'])
      if(this.headerIsShow && this.handles.indexOf(h)>=0)
        this.handles.splice(this.handles.indexOf(h), 1)
    for(let h of ['mr', 'ml'])
      if(this.axis != 'x' && this.axis != 'both' && this.handles.indexOf(h)>=0)
        this.handles.splice(this.handles.indexOf(h), 1)
    for(let h of ['tm', 'bm'])
      if(this.axis != 'y' && this.axis != 'both' && this.handles.indexOf(h)>=0)
        this.handles.splice(this.handles.indexOf(h), 1)
  },
  mounted() {
    this.Geom      = new Geom(this.geom, this.sizeParent, this.sizeLim)
    this.mousePos  = {x: 0, y:0}
    this.mouseOff  = {x: 0, y:0}
    this.mouseDown = false
    const doc = document.documentElement
    doc.addEventListener('mousedown', this._mouseDown, true)
    doc.addEventListener('mousemove', this._mouseMove, true)
    doc.addEventListener('mouseup', this._mouseUp, true)
    this.$bus.on(`float#${this.id}.open`, this._open)
  },
  beforeDestroy() {
    const doc = document.documentElement
    doc.removeEventListener('mousedown', this._mouseDown, true)
    doc.removeEventListener('mousemove', this._mouseMove, true)
    doc.removeEventListener('mouseup', this._mouseUp, true)
    this.$bus.off(`float#${this.id}.open`, this._open)
  },
  methods: {
    _mouseDown(e, handle=null) {
      this.mouseDown = true
      this.handle = handle
      if(this.handle)
        this.resizing = true
      else
        this._activate(e)
    },
    _mouseMove(e) {
      let mousePosNew = {
        x: e.pageX || e.clientX + document.documentElement.scrollLeft,
        y: e.pageY || e.clientY + document.documentElement.scrollTop
      }
      let mouseMov = {
        x: mousePosNew.x - this.mousePos.x - this.mouseOff.x,
        y: mousePosNew.y - this.mousePos.y - this.mouseOff.y
      }
      let objMov = mouseMov
      this.mousePos = mousePosNew
      if(this.activated && this.mouseDown && !this.resizing)
        this.dragging = true
      if(this.dragging)
        objMov = this._drag(e, mouseMov)
      if(this.resizing)
        objMov = this._resize(e, mouseMov)
      this.mouseOff = {
        x: objMov.x - mouseMov.x,
        y: objMov.y - mouseMov.y
      }
    },
    _mouseUp(e) {
      if(this.resizing)
        this.$emit('resized', this.geom)
      if(this.dragging)
        this.$emit('dragged', this.geom)
      if(this.activated && this.mouseDown && (this.dragging || this.resizing)) {
        this.activated = false
        this.$emit('deactivated', this.geom)
      }
      this.handle    = null
      this.mouseDown = false
      this.dragging  = false
      this.resizing  = false
    },
    _activate(e) {
      const elRoot = !this.headerIsShow ? this.$el : this.$refs.floatHeader
      const elCurr = e.target || e.srcElement
      const isInside = elRoot.contains(elCurr)
      if( isInside && !this.activated) {
        this.activated = true
        this.zIndex+= 1
        this.$emit('activated', this.geom)
      }
      if(!isInside &&  this.activated) {
        this.activated = false
        this.$emit('deactivated', this.geom)
      }
    },
    _maximize(e) {
      if(this.maximized) {
        this.Geom.minimize({x: this.deltx, y: this.delty})
        this.maximized = false
        setTimeout(() => this.$emit('minimized', this.geom), 500)
      }
      else {
        this.Geom.maximize({x: this.deltx, y: this.delty})
        this.maximized = true
        setTimeout(() => this.$emit('maximized', this.geom), 500)
      }
    },
    _drag(e, mouseMov={x: 0, y: 0}) {
      return this.Geom.move({
        x: (this.axis === 'x' || this.axis === 'both') ? mouseMov.x : 0,
        y: (this.axis === 'y' || this.axis === 'both') ? mouseMov.y : 0
      })
    },
    _resize(e, mouseMov={x: 0, y: 0}) {
      let dir = {
        x: this.handle[1]=='r' ? 1 : this.handle[1]=='l' ? -1 : 0,
        y: this.handle[0]=='b' ? 1 : this.handle[0]=='t' ? -1 : 0
      }
      let objRes = this.Geom.resize(mouseMov, dir)

      /*
      console.log('---- ', this.handle)
      console.log(this.dragging)
      console.log(this.resizing)
      console.log('mouse: ', mouseMov)
      console.log('obj x dlen:   ', objRes.x.dlen)
      console.log('obj x dpos:   ', objRes.x.dpos)
      console.log('dir:   ', dir)
      */
      return {x: objRes.x.dlen, y: objRes.y.dlen}
    },
    _open(e) {
      this.openedSelf = true
      this.$emit('opened', this.geom)
    },
    _close(e, save=true) {
      this.openedSelf = false
      let params = {
        geom: this.geom,
        save: save,
        value: this.value_,
        valueOld: this.value
      }
      if(this.closeSelf)
        this.$emit('closed', params)
      else
        this.$emit('close-request', params)
    }
  }
}
</script>

<style lang="sass" scoped>

$handle_w: 10px
$handle_h: 10px
$handle_dw: 0px // $handle_w/2
$handle_dh: 0px // $handle_h/2
$handle_color_bg: #EEE
$handle_color_bd: #333

.float
  box-sizing: border-box
  display: flex
  flex-direction: column
  padding: ($handle_w - $handle_dh) ($handle_h - $handle_dw)
  background-color: black
  &__header
    width: 100%
    display: flex
    justify-content: space-between
    background-color: green
    &_drag
      &:hover
        cursor: move
  &__header-toolbar
    &_main
  &__content
    width: 100%
    display: flex
    overflow: scroll
    background-color: grey
    &_drag
      &:hover
        cursor: move
  &__text
  &__handle
    box-sizing: border-box
    position: absolute
    width: $handle_w
    height: $handle_h
    background-color: $handle_color_bg
    border: 1px solid $handle_color_bd
    &_type_tl, &_type_tm, &_type_tr
      top: -$handle_dh
    &_type_tr, &_type_mr, &_type_br
      right: -$handle_dw
    &_type_bl, &_type_bm, &_type_br
      bottom: -$handle_dh
    &_type_tl, &_type_ml, &_type_bl
      left: -$handle_dw
    &_type_tm, &_type_bm
      left: 50%
      margin-left: -$handle_w/2
    &_type_ml, &_type_mr
      top: 50%
      margin-top: -$handle_h/2
    &_type_tl
      cursor: nw-resize
    &_type_tm
      cursor: n-resize
    &_type_tr
      cursor: ne-resize
    &_type_ml
      cursor: w-resize
    &_type_mr
      cursor: e-resize
    &_type_bl
      cursor: sw-resize
    &_type_bm
      cursor: s-resize
    &_type_br
      cursor: se-resize
</style>



// WEBPACK FOOTER //
// src/float/vue/Float.vue